import React, { useState } from 'react';
import { passwordResetApi } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, ArrowLeft, Mail, KeyRound, Loader2 } from 'lucide-react';

export function PasswordResetFlow({ onCancel, initialEmail, bgImage, logoUrl }) {
  const [step, setStep] = useState('email'); // 'email', 'code', 'newPassword'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState(initialEmail || '');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!resetEmail) { toast.error('Veuillez entrer votre adresse email'); return; }
    setLoading(true);
    try {
      await passwordResetApi.forgotPassword(resetEmail);
      toast.success('Si cette adresse existe, un code de vérification a été envoyé');
      setStep('code');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du code');
    } finally { setLoading(false); }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (resetCode.length !== 6) { toast.error('Le code doit contenir 6 chiffres'); return; }
    setLoading(true);
    try {
      await passwordResetApi.verifyCode(resetEmail, resetCode);
      setStep('newPassword');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code de vérification invalide');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Le mot de passe doit contenir au moins 8 caractères'); return; }
    if (newPassword !== confirmNewPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      await passwordResetApi.resetPassword(resetEmail, resetCode, newPassword);
      toast.success('Mot de passe réinitialisé avec succès !');
      onCancel(resetEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally { setLoading(false); }
  };

  const SidePanel = ({ title, description }) => (
    <div className="hidden lg:block lg:w-1/2 bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }}>
      <div className="h-full w-full bg-[#1e3a8a]/60 flex items-center justify-center p-12">
        <div className="text-center text-white max-w-lg">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>{title}</h1>
          <p className="text-lg text-white/90">{description}</p>
        </div>
      </div>
    </div>
  );

  if (step === 'email') {
    return (
      <div className="min-h-screen flex" data-testid="reset-email-page">
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-8">
              <img src={logoUrl} alt="Diocèse de Pamiers" className="h-24 w-24 rounded-full shadow-lg bg-white p-2"/>
            </div>
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4"><div className="p-3 bg-amber-100 rounded-full"><Mail className="h-8 w-8 text-amber-600" /></div></div>
                <CardTitle className="text-2xl text-[#1e3a8a]">Mot de passe oublié ?</CardTitle>
                <CardDescription className="text-slate-600">Entrez votre adresse email pour recevoir un code de vérification</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset_email" className="text-slate-700">Adresse email</Label>
                    <Input id="reset_email" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="votre.email@exemple.fr" className="border-slate-200 focus:ring-[#c5a059]" autoFocus data-testid="reset-email-input" />
                  </div>
                  <Button type="submit" disabled={loading || !resetEmail} className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white" data-testid="send-reset-code-button">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Envoyer le code
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => onCancel()} className="w-full text-slate-600"><ArrowLeft className="mr-2 h-4 w-4" /> Retour à la connexion</Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
        <SidePanel title="Réinitialisation" description="Un code de vérification sera envoyé à votre adresse email." />
      </div>
    );
  }

  if (step === 'code') {
    return (
      <div className="min-h-screen flex" data-testid="reset-code-page">
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-8">
              <img src={logoUrl} alt="Diocèse de Pamiers" className="h-24 w-24 rounded-full shadow-lg bg-white p-2"/>
            </div>
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4"><div className="p-3 bg-blue-100 rounded-full"><KeyRound className="h-8 w-8 text-blue-600" /></div></div>
                <CardTitle className="text-2xl text-[#1e3a8a]">Code de vérification</CardTitle>
                <CardDescription className="text-slate-600">Entrez le code à 6 chiffres envoyé à {resetEmail}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerifyCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset_code" className="text-slate-700">Code de vérification</Label>
                    <Input id="reset_code" type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="border-slate-200 focus:ring-[#c5a059] text-center text-2xl tracking-widest font-mono" maxLength={6} autoFocus data-testid="reset-code-input" />
                    <p className="text-xs text-slate-500 text-center">Le code est valide pendant 15 minutes</p>
                  </div>
                  <Button type="submit" disabled={loading || resetCode.length !== 6} className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white" data-testid="verify-reset-code-button">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />} Vérifier le code
                  </Button>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setStep('email')} className="flex-1">Renvoyer le code</Button>
                    <Button type="button" variant="ghost" onClick={() => onCancel()} className="flex-1 text-slate-600">Annuler</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
        <SidePanel title="Vérification" description="Consultez votre boîte mail pour trouver le code de vérification." />
      </div>
    );
  }

  // Step: newPassword
  return (
    <div className="min-h-screen flex" data-testid="reset-newpassword-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <img src={logoUrl} alt="Diocèse de Pamiers" className="h-24 w-24 rounded-full shadow-lg bg-white p-2"/>
          </div>
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4"><div className="p-3 bg-emerald-100 rounded-full"><KeyRound className="h-8 w-8 text-emerald-600" /></div></div>
              <CardTitle className="text-2xl text-[#1e3a8a]">Nouveau mot de passe</CardTitle>
              <CardDescription className="text-slate-600">Choisissez un nouveau mot de passe sécurisé</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new_password" className="text-slate-700">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input id="new_password" type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="border-slate-200 focus:ring-[#c5a059] pr-10" autoFocus data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Minimum 8 caractères</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_new_password" className="text-slate-700">Confirmer le mot de passe</Label>
                  <Input id="confirm_new_password" type={showPassword ? "text" : "password"} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="••••••••" className="border-slate-200 focus:ring-[#c5a059]" data-testid="confirm-new-password-input" />
                </div>
                <Button type="submit" disabled={loading || newPassword.length < 8 || newPassword !== confirmNewPassword} className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white" data-testid="reset-password-button">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />} Réinitialiser le mot de passe
                </Button>
                <Button type="button" variant="ghost" onClick={() => onCancel()} className="w-full text-slate-600">Annuler</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      <SidePanel title="Presque terminé !" description="Choisissez un mot de passe fort pour protéger votre compte." />
    </div>
  );
}
